import React, { useMemo } from "react";
import Skeleton from "@mui/material/Skeleton";
import AutoSizer from "react-virtualized-auto-sizer";
import { Box, Theme, styled, useMediaQuery } from "@mui/material";
import { useChartColors } from "@hooks";
import {
  DonutChart,
  DonutChartItem,
} from "../highcharts/DonutChart/DonutChart";
import { AssetsChartDataItem } from "./types";

const StyledSkeleton = styled(Skeleton)(() => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
}));

export interface AssetsChartProps {
  data: AssetsChartDataItem[];
  isFetching?: boolean;
}

export const AssetsChart = (props: AssetsChartProps) => {
  const { data, isFetching, ...rest } = props;
  const colors = useChartColors("assetsChart");
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const noData = data.every((item) => item.value === 0);
  const chartData = useMemo(
    () =>
      data.map(
        (x) =>
          ({
            ...x,
            id: x.name,
            currency: "",
            color: colors[x.name],
            y: x.value,
          }) as DonutChartItem
      ),
    [data, colors]
  );

  if (!isFetching && noData) {
    return (
      <Box position="relative" width="100%" height="320px">
        <StyledSkeleton
          variant="circular"
          animation={isFetching ? "pulse" : false}
          sx={{
            backgroundColor:
              !isFetching && noData
                ? "background.neutral"
                : "background.subtle",
            zIndex: 1,
            width: "260px",
            height: "260px",
          }}
        />
        <StyledSkeleton
          variant="circular"
          animation={false}
          sx={{
            backgroundColor: "background.default",
            zIndex: 2,
            width: "100px",
            height: "100px",
          }}
        />
      </Box>
    );
  }

  return (
    <Box width="100%" height={isSm ? 320 : "auto"} minHeight={320} {...rest}>
      <AutoSizer>
        {({ width, height }: { width: number; height: number }) => (
          <Box
            width={width}
            height={height}
            display="flex"
            justifyContent="center"
          >
            <DonutChart
              chartData={chartData}
              height={height}
              width={width}
              isLoading={isFetching}
            />
          </Box>
        )}
      </AutoSizer>
    </Box>
  );
};
